import { SuiSortState as Sort } from '@semeris/ui';
import { first, groupBy, max, min, orderBy, sumBy } from 'lodash-es';

import { DocumentDefinedTermViewModel, TermPosition } from '../models/document-defined-term';
import { TermExplorerItem, TermExplorerSynonym, TermSynonymGroupCounts } from '../models/term-explorer.model';

/** Everything the Term Explorer list is derived from. */
export interface TermExplorerListSource {
  originalDocumentId: string;
  originalTerms: DocumentDefinedTermViewModel[];
  finalTerms: DocumentDefinedTermViewModel[];
  finalDocumentId: string;
  occurences: Record<string, number>;
  synonymGroups: Record<string, TermSynonymGroupCounts>;
  groupSynonyms: boolean;
  sort: Sort;
  focusMode: boolean;
  loadingOccurencies: boolean;
}

type UnscaledSynonym = Omit<TermExplorerSynonym, 'frequency'>;
type UnscaledItem = Omit<TermExplorerItem, 'frequency' | 'synonyms'> & { synonyms?: UnscaledSynonym[] };

/** Builds the Term Explorer rows, merging each synonym ring into one row per diff position when grouping is on. */
export function buildTermExplorerList(source: TermExplorerListSource): TermExplorerItem[] {
  const termsDictionary = groupBy([...(source.finalTerms || []), ...(source.originalTerms || [])], (t) => t.definedTerm);
  const termItems = Object.keys(termsDictionary).map((term) => toTermItem(term, termsDictionary, source));
  const items = source.groupSynonyms ? groupBySynonyms(termItems, termsDictionary, source) : termItems;

  return orderBy(withFrequency(items), [source.sort.active], [source.sort.direction as 'asc' | 'desc']).filter(
    (t) => !source.focusMode || t.position !== TermPosition.Both,
  );
}

/** Adds one chunk's synonym ring counts to the counts loaded so far. */
export function mergeSynonymGroupCounts(
  current: Record<string, TermSynonymGroupCounts>,
  incoming: Record<string, TermSynonymGroupCounts>,
): Record<string, TermSynonymGroupCounts> {
  const merged = Object.entries(incoming).map(([id, group]): [string, TermSynonymGroupCounts] => {
    const existing = current?.[id];
    return [id, existing ? { occurence: existing.occurence + group.occurence, terms: sumCounts(existing.terms, group.terms) } : group];
  });
  return { ...current, ...Object.fromEntries(merged) };
}

/** Whether the occurrence counts are still on their way, including before they are first requested. */
export function areCountsPending(loadingOccurencies: boolean, occurences: Record<string, number>): boolean {
  return loadingOccurencies || !occurences;
}

function toTermItem(
  term: string,
  termsDictionary: Record<string, DocumentDefinedTermViewModel[]>,
  source: TermExplorerListSource,
): UnscaledItem {
  const base = first(termsDictionary[term]);

  return {
    definedTerm: base.definedTerm,
    documentShortName: base.documentShortName,
    libraryShortName: base.libraryShortName,
    documentCount: areCountsPending(source.loadingOccurencies, source.occurences) ? undefined : source.occurences[term] || 0,
    transactionShortName: base.transactionShortName,
    primaryDomOrder: base.primaryDomOrder,
    primaryDomId: base.primaryDomId,
    position: positionOf(term, termsDictionary, source),
  };
}

function positionOf(
  term: string,
  termsDictionary: Record<string, DocumentDefinedTermViewModel[]>,
  source: TermExplorerListSource,
): TermPosition {
  const isDiffMode = !!source.originalDocumentId && !!source.originalTerms;
  if (!isDiffMode) {
    return null;
  }
  if (termsDictionary[term].length === 2) {
    return TermPosition.Both;
  }
  return first(termsDictionary[term]).documentShortName === source.finalDocumentId ? TermPosition.Final : TermPosition.Original;
}

function groupBySynonyms(
  items: UnscaledItem[],
  termsDictionary: Record<string, DocumentDefinedTermViewModel[]>,
  source: TermExplorerListSource,
): UnscaledItem[] {
  const groupIdByTerm = new Map(
    Object.entries(source.synonymGroups || {}).flatMap(([id, group]) => Object.keys(group.terms).map((term) => [term, id] as const)),
  );
  const buckets = groupBy(items, (item) =>
    groupIdByTerm.has(item.definedTerm) ? `${groupIdByTerm.get(item.definedTerm)}|${item.position}` : '',
  );
  const itemByTerm = new Map(items.map((item) => [item.definedTerm, item]));

  return Object.entries(buckets).flatMap(([key, bucket]) =>
    key
      ? [toGroupItem(bucket, source.synonymGroups[groupIdByTerm.get(bucket[0].definedTerm)], termsDictionary, itemByTerm, source)]
      : bucket,
  );
}

function toGroupItem(
  bucket: UnscaledItem[],
  group: TermSynonymGroupCounts,
  termsDictionary: Record<string, DocumentDefinedTermViewModel[]>,
  itemByTerm: Map<string, UnscaledItem>,
  source: TermExplorerListSource,
): UnscaledItem {
  const [primary] = orderBy(bucket, [(item) => item.documentCount ?? 0, 'definedTerm'], ['desc', 'asc']);
  const synonyms: UnscaledSynonym[] = orderBy(Object.entries(group.terms), [([, count]) => count, ([term]) => term], ['desc', 'asc']).map(
    ([term, count]) => ({
      definedTerm: term,
      documentCount: count,
      inDocument: !!termsDictionary[term],
      primaryDomId: itemByTerm.get(term)?.primaryDomId,
      position: itemByTerm.get(term)?.position,
    }),
  );

  const documentCount = areCountsPending(source.loadingOccurencies, source.occurences) ? undefined : group.occurence;

  if (synonyms.length < 2) {
    return { ...primary, documentCount };
  }

  const otherWordings = synonyms.filter((synonym) => synonym.definedTerm !== primary.definedTerm);
  return {
    ...primary,
    documentCount,
    synonyms,
    synonymText: otherWordings.map((synonym) => synonym.definedTerm).join(' '),
    otherWordingsCount: documentCount === undefined ? undefined : sumBy(otherWordings, 'documentCount'),
  };
}

function withFrequency(items: UnscaledItem[]): TermExplorerItem[] {
  const counts = items.map((item) => item.documentCount).filter((count) => count > 0);
  const maxCount = max(counts);
  const minCount = min(counts);
  const frequency = (count: number) => (count === minCount ? 0 : Math.sqrt(count / maxCount) * 100);

  return items.map((item) => ({
    ...item,
    frequency: frequency(item.documentCount),
    synonyms: item.synonyms?.map((synonym) => ({ ...synonym, frequency: frequency(synonym.documentCount) })),
  }));
}

function sumCounts(a: Record<string, number>, b: Record<string, number>): Record<string, number> {
  const terms = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Object.fromEntries([...terms].map((term) => [term, (a[term] ?? 0) + (b[term] ?? 0)]));
}
